import {
  CustomUnitVM,
  GlobalResponsesVM,
  GroupedItemsVM,
  ImageMarkersVM,
  InfoVM,
  RatingVM,
  ResponsesVM,
  RuleSetsVM
} from "@cmodules/checklist/assets/data/types"
import { AnyItemVM, ItemVM } from "@cmodules/checklist/assets/data/types/vm/template-builder/item"
import { ServerItemType } from "./server-item-type"

const itemTypes = {
  page: 'page',
  section: 'section',
  item: 'item',
  custom: 'custom',
  global_start_page: 'start_page',
  global_end_page: 'end_page'
} as const

type GlobalItemType = typeof itemTypes[keyof typeof itemTypes]
const GlobalItemTypesArray: GlobalItemType[] = Object.values(itemTypes)


class TemplateOptionVM {
  has_draft: boolean;
  published: boolean;
  version_title: string | number;
  version_id: string;
  language: string | null;
  contains_asset: boolean;
  template_type: GlobalItemType | null;
  template_main_item_base_id: string | null;
  template_main_item_type: ServerItemType | null;

  constructor(data: Partial<TemplateOptionVM> = {}) {
    const {
      has_draft = false,
      published = false,
      version_title = "",
      version_id = "",
      language = null,
      contains_asset = false,
      template_type = null,
      template_main_item_base_id = null,
      template_main_item_type = null
    } = data
    this.has_draft = has_draft
    this.published = published
    this.version_title = version_title
    this.version_id = version_id
    this.language = language
    this.contains_asset = contains_asset
    this.template_type = template_type
    this.template_main_item_base_id = template_main_item_base_id
    this.template_main_item_type = template_main_item_type
  }
}

class TemplateVM {
  id: number;
  title: string;
  description: string;
  is_template: boolean;
  revision: string;
  options: TemplateOptionVM;
  parent_id: number | null;
  created_at: string;
  updated_at: string;
  responses: ResponsesVM;
  info: InfoVM;
  image_markers: ImageMarkersVM;
  grouped_items: GroupedItemsVM;
  rule_sets: RuleSetsVM;
  translations: string[];
  ratings: RatingVM[];
  custom_units: CustomUnitVM[];
  global_responses: GlobalResponsesVM;
  items: AnyItemVM[];
  template_id: string;
  language: string | null;
  contains_asset: boolean;
  template_main_item_type: ServerItemType | null;
  has_draft_translations: boolean;
  act_as_global_items: boolean;


  constructor(templateVM: Partial<TemplateVM> = {}) {
    const {
      id = 0,
      title = '',
      description = '',
      is_template = true,
      revision = '',
      options = new TemplateOptionVM(),
      parent_id = null,
      created_at = '',
      updated_at = '',
      responses = new ResponsesVM(),
      info = new InfoVM(),
      image_markers = new ImageMarkersVM(),
      grouped_items = new GroupedItemsVM(),
      rule_sets = new RuleSetsVM(),
      translations = [],
      ratings = [],
      custom_units = [],
      global_responses = {},
      items = [],
      template_id = '',
      language = null,
      contains_asset = false,
      template_main_item_type = null,
      has_draft_translations = false,
      act_as_global_items = false
    } = templateVM || {}

    this.id = id
    this.title = title
    this.description = description
    this.is_template = is_template
    this.revision = revision
    this.options = options
    this.parent_id = parent_id
    this.created_at = created_at
    this.updated_at = updated_at
    this.responses = responses
    this.info = info
    this.image_markers = image_markers
    this.grouped_items = grouped_items
    this.rule_sets = rule_sets
    this.translations = translations
    this.ratings = ratings
    this.custom_units = custom_units
    this.global_responses = global_responses
    this.items = (items ?? []).map(item => item as AnyItemVM)
    /*
      ### IMPORTANT FIX
      ItemVM resolves the correct options constructor from OptionsRegistry via item.type
      This should be uncommented and tested
    */
    // this.items = (items ?? []).map(item => new ItemVM(item) as AnyItemVM)
    this.template_id = template_id
    this.language = language
    this.contains_asset = contains_asset
    this.template_main_item_type = template_main_item_type
    this.has_draft_translations = has_draft_translations
    this.act_as_global_items = act_as_global_items
  }
}

export type { GlobalItemType }

export { GlobalItemTypesArray, TemplateOptionVM, TemplateVM }

